// src/services/projectNotes.service.ts
import { db, storage } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  ProjectNote,
  NoteType,
  NoteVisibility,
  DutyAcceptance,
  ProjectNoteFilter,
  CreateNoteDto,
  NoteStats
} from '../types/projectNotes.types';

export const projectNotesService = {
  async createNote(
    data: CreateNoteDto,
    projectName: string,
    authorId: string,
    authorName: string,
    authorRole: string
  ): Promise<string> {
    const noteRef = doc(collection(db, 'projectNotes'));

    // Upload attachments if any
    let attachments: { name: string; url: string; type: string; size: number }[] = [];
    if (data.attachments && data.attachments.length > 0) {
      for (const file of data.attachments) {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const storageRef = ref(storage, `projectNotes/${noteRef.id}/${fileName}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        attachments.push({
          name: file.name,
          url,
          type: file.type,
          size: file.size
        });
      }
    }

    const note: ProjectNote = {
      id: noteRef.id,
      projectId: data.projectId,
      projectName,
      type: data.type,
      title: data.title || null,
      content: data.content,
      visibility: data.visibility,
      ...(data.parentNoteId && { parentNoteId: data.parentNoteId }),
      replyCount: 0,
      authorId,
      authorName,
      authorRole,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      isEdited: false,
      ...(attachments.length > 0 && { attachments }),
      ...(data.mentions && data.mentions.length > 0 && { mentions: data.mentions }),
      isPinned: false
    };

    await setDoc(noteRef, note);

    // If this is a reply, increment parent's reply count
    if (data.parentNoteId) {
      await updateDoc(doc(db, 'projectNotes', data.parentNoteId), {
        replyCount: increment(1)
      });
    }

    return noteRef.id;
  },

  async getNote(noteId: string): Promise<ProjectNote | null> {
    const docRef = doc(db, 'projectNotes', noteId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as ProjectNote;
    }
    return null;
  },

  async getNotes(filters: ProjectNoteFilter, userRole: string, userId: string): Promise<ProjectNote[]> {
    // Project notes are provider-side only
    if (!['provider_admin', 'provider_user'].includes(userRole)) {
      return [];
    }

    let q = query(
      collection(db, 'projectNotes'),
      where('projectId', '==', filters.projectId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    let notes = snapshot.docs.map(doc => doc.data() as ProjectNote);

    // Filter by visibility
    notes = notes.filter(n => {
      // Provider team notes are visible to all provider roles
      if (n.visibility === 'provider_team') return true;
      // Me only notes are only visible to the author
      if (n.visibility === 'me_only') {
        return n.authorId === userId;
      }
      return true;
    });

    // Apply additional filters
    if (filters.type && filters.type.length > 0) {
      notes = notes.filter(n => filters.type!.includes(n.type));
    }

    if (filters.visibility && filters.visibility.length > 0) {
      notes = notes.filter(n => filters.visibility!.includes(n.visibility));
    }

    if (filters.authorId) {
      notes = notes.filter(n => n.authorId === filters.authorId);
    }

    if (filters.parentNoteId !== undefined) {
      if (filters.parentNoteId === null) {
        // Top-level notes only
        notes = notes.filter(n => !n.parentNoteId);
      } else {
        // Replies to specific note
        notes = notes.filter(n => n.parentNoteId === filters.parentNoteId);
      }
    }

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      notes = notes.filter(n =>
        n.content.toLowerCase().includes(term) ||
        (n.title && n.title.toLowerCase().includes(term))
      );
    }

    if (filters.pinnedOnly) {
      notes = notes.filter(n => n.isPinned);
    }

    return notes;
  },

  async getNoteReplies(parentNoteId: string, userRole: string, userId: string): Promise<ProjectNote[]> {
    // Project notes are provider-side only
    if (!['provider_admin', 'provider_user'].includes(userRole)) {
      return [];
    }

    const q = query(
      collection(db, 'projectNotes'),
      where('parentNoteId', '==', parentNoteId),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    let notes = snapshot.docs.map(doc => doc.data() as ProjectNote);

    // Filter by visibility
    notes = notes.filter(n => {
      if (n.visibility === 'provider_team') return true;
      if (n.visibility === 'me_only') {
        return n.authorId === userId;
      }
      return true;
    });

    return notes;
  },

  async updateNote(noteId: string, content: string, title?: string): Promise<void> {
    await updateDoc(doc(db, 'projectNotes', noteId), {
      content,
      ...(title && { title }),
      isEdited: true,
      editedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  },

  async deleteNote(noteId: string): Promise<void> {
    const note = await this.getNote(noteId);
    if (note) {
      // Delete attachments
      if (note.attachments) {
        for (const attachment of note.attachments) {
          try {
            const storageRef = ref(storage, attachment.url);
            await deleteObject(storageRef);
          } catch (e) {
            // File may not exist
          }
        }
      }

      // Decrement parent's reply count if this is a reply
      if (note.parentNoteId) {
        await updateDoc(doc(db, 'projectNotes', note.parentNoteId), {
          replyCount: increment(-1)
        });
      }

      await deleteDoc(doc(db, 'projectNotes', noteId));
    }
  },

  async togglePinNote(noteId: string, pin: boolean, userId: string): Promise<void> {
    await updateDoc(doc(db, 'projectNotes', noteId), {
      isPinned: pin,
      pinnedAt: pin ? Timestamp.now() : null,
      pinnedBy: pin ? userId : null,
      updatedAt: Timestamp.now()
    });
  },

  async addReaction(noteId: string, emoji: string, userId: string): Promise<void> {
    const note = await this.getNote(noteId);
    if (!note) throw new Error('Note not found');

    const reactions = note.reactions || {};
    const emojiUsers = reactions[emoji] || [];

    if (!emojiUsers.includes(userId)) {
      emojiUsers.push(userId);
      reactions[emoji] = emojiUsers;

      await updateDoc(doc(db, 'projectNotes', noteId), {
        reactions,
        updatedAt: Timestamp.now()
      });
    }
  },

  async removeReaction(noteId: string, emoji: string, userId: string): Promise<void> {
    const note = await this.getNote(noteId);
    if (!note) throw new Error('Note not found');

    const reactions = note.reactions || {};
    const emojiUsers = reactions[emoji] || [];

    const index = emojiUsers.indexOf(userId);
    if (index > -1) {
      emojiUsers.splice(index, 1);
      if (emojiUsers.length === 0) {
        delete reactions[emoji];
      } else {
        reactions[emoji] = emojiUsers;
      }

      await updateDoc(doc(db, 'projectNotes', noteId), {
        reactions,
        updatedAt: Timestamp.now()
      });
    }
  },

  async createDutyAcceptance(
    projectId: string,
    projectName: string,
    userId: string,
    userName: string,
    userRole: string,
    dutiesDescription: string,
    responsibilities: string[],
    additionalComments?: string
  ): Promise<string> {
    const acceptanceRef = doc(collection(db, 'projectDutyAcceptance'));
    const acceptance: DutyAcceptance = {
      id: acceptanceRef.id,
      projectId,
      projectName,
      userId,
      userName,
      userRole,
      dutiesDescription,
      responsibilities,
      acceptedAt: Timestamp.now(),
      additionalComments
    };

    await setDoc(acceptanceRef, acceptance);

    // Create associated note (visible to provider team)
    const noteId = await this.createNote(
      {
        projectId,
        type: 'duty_acceptance',
        title: `${userName} accepted project duties`,
        content: `${userName} (${userRole}) has accepted the following duties:\n\n${dutiesDescription}\n\nResponsibilities:\n${responsibilities.map(r => `- ${r}`).join('\n')}${additionalComments ? `\n\nComments: ${additionalComments}` : ''}`,
        visibility: 'provider_team'
      },
      projectName,
      userId,
      userName,
      userRole
    );

    // Link note to acceptance
    await updateDoc(acceptanceRef, { noteId });

    return acceptanceRef.id;
  },

  async getProjectDutyAcceptances(projectId: string): Promise<DutyAcceptance[]> {
    const q = query(
      collection(db, 'projectDutyAcceptance'),
      where('projectId', '==', projectId),
      orderBy('acceptedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as DutyAcceptance);
  },

  async getUserDutyAcceptances(userId: string): Promise<DutyAcceptance[]> {
    const q = query(
      collection(db, 'projectDutyAcceptance'),
      where('userId', '==', userId),
      orderBy('acceptedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as DutyAcceptance);
  },

  async getNoteStats(projectId: string): Promise<NoteStats> {
    const q = query(
      collection(db, 'projectNotes'),
      where('projectId', '==', projectId)
    );
    const snapshot = await getDocs(q);
    const notes = snapshot.docs.map(doc => doc.data() as ProjectNote);

    const byType: Record<NoteType, number> = {
      general: 0,
      duty_acceptance: 0,
      update: 0,
      issue: 0,
      resolution: 0,
      client_feedback: 0,
      internal: 0
    };

    const contributorCounts: Record<string, { name: string; count: number }> = {};
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    let recentActivity = 0;

    notes.forEach(n => {
      byType[n.type]++;

      if (!contributorCounts[n.authorId]) {
        contributorCounts[n.authorId] = { name: n.authorName, count: 0 };
      }
      contributorCounts[n.authorId].count++;

      if (n.createdAt.toDate() >= sevenDaysAgo) {
        recentActivity++;
      }
    });

    const topContributors = Object.entries(contributorCounts)
      .map(([userId, data]) => ({
        userId,
        userName: data.name,
        noteCount: data.count
      }))
      .sort((a, b) => b.noteCount - a.noteCount)
      .slice(0, 5);

    return {
      totalNotes: notes.length,
      byType,
      recentActivity,
      topContributors
    };
  }
};
